/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * SERVICE: laudoApi
 * VERSÃO:  2.3.0
 * ------------------------------------------------------------
 * NOVIDADES v2.3.0:
 *   ✔ Endpoints de tarefas movidos para /api/tarefas/
 * ============================================================
 */

const BASE_URL = '';
const TIMEOUT_MS = 15000;
const RETRY_ATTEMPTS = 3;
const BACKOFF_BASE_MS = 500;
const FILA_KEY = 'mh_fila_sincronizacao_v2';

/* =========================
   RETRY
========================= */

async function fetchComRetry(url, options, tentativa = 0) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const resp = await fetch(url, { ...options, signal: controller.signal });
    if (!resp.ok && resp.status >= 500 && tentativa < RETRY_ATTEMPTS - 1) {
      throw new Error(`HTTP_${resp.status}`);
    }
    return resp;
  } catch (err) {
    if (tentativa < RETRY_ATTEMPTS - 1 && err.name !== 'AbortError') {
      await new Promise(r => setTimeout(r, BACKOFF_BASE_MS * Math.pow(2, tentativa)));
      return fetchComRetry(url, options, tentativa + 1);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/* =========================
   FILA OFFLINE
========================= */

function enfileirar(payload) {
  try {
    const fila = JSON.parse(localStorage.getItem(FILA_KEY) || '[]');
    fila.push({ ...payload, _enfileiradoEm: new Date().toISOString() });
    localStorage.setItem(FILA_KEY, JSON.stringify(fila));
  } catch (e) {
    console.error('[laudoApi] Falha ao enfileirar', e);
  }
}

export function lerFila() {
  try { return JSON.parse(localStorage.getItem(FILA_KEY) || '[]'); }
  catch { return []; }
}

export function limparFila() {
  localStorage.removeItem(FILA_KEY);
}

/* =========================
   HELPERS
========================= */

function headersBase(extra = {}, soberaniaId = null) {
  const h = { 'Content-Type': 'application/json', ...extra };
  if (soberaniaId) h['X-Soberania-ID'] = soberaniaId;
  return h;
}

async function tratarResposta(resp) {
  const texto = await resp.text().catch(() => '');
  let json = {};
  try { json = texto ? JSON.parse(texto) : {}; } catch { json = { _raw: texto }; }

  if (!resp.ok) {
    const err = new Error(json.erro || json.error || `HTTP_${resp.status}`);
    err.status = resp.status;
    err.payload = json;
    throw err;
  }
  return json;
}

/* =========================
   API PÚBLICA
========================= */

export const laudoApi = {

  /* ---------- MÓDULOS (router universal) ---------- */

  async salvarModulo(pacote) {
    try {
      const resp = await fetchComRetry(`${BASE_URL}/api/laudos/salvar.php`, {
        method: 'POST',
        headers: headersBase(),
        credentials: 'include',
        body: JSON.stringify({
          ...pacote,
          _versao_schema: '6.0.1',
          _timestamp_cliente: new Date().toISOString(),
        }),
      });
      return await tratarResposta(resp);
    } catch (err) {
      if (!err.status || err.status >= 500) {
        enfileirar(pacote);
        throw Object.assign(err, { _enfileirado: true });
      }
      throw err;
    }
  },

  /* ---------- LAUDO ---------- */

  async criarLaudo({ objetivo, finalidade, esfera, requerente = '' }) {
    const resp = await fetchComRetry(`${BASE_URL}/api/laudos/criar.php`, {
      method: 'POST',
      headers: headersBase(),
      credentials: 'include',
      body: JSON.stringify({ objetivo, finalidade, esfera, requerente }),
    });
    return tratarResposta(resp);
  },

  async atualizarLaudo({ codigo_mh, objetivo, finalidade, esfera }) {
    const resp = await fetchComRetry(`${BASE_URL}/api/laudos/atualizar.php`, {
      method: 'POST',
      headers: headersBase(),
      credentials: 'include',
      body: JSON.stringify({ codigo_mh, objetivo, finalidade, esfera }),
    });
    return tratarResposta(resp);
  },

  async buscarLaudo(codigo) {
    const resp = await fetchComRetry(
      `${BASE_URL}/api/laudos/buscar.php?codigo=${encodeURIComponent(codigo)}`,
      { credentials: 'include' }
    );
    return tratarResposta(resp);
  },

  async listarLaudos() {
    const resp = await fetchComRetry(`${BASE_URL}/api/laudos/listar.php`, {
      credentials: 'include',
    });
    const data = await tratarResposta(resp);
    return Array.isArray(data) ? data : (data.laudos || []);
  },

  async carregarLaudo(codigo) {
    const resp = await fetchComRetry(
      `${BASE_URL}/api/laudos/index.php?codigo=${encodeURIComponent(codigo)}`,
      { credentials: 'include' }
    );
    return tratarResposta(resp);
  },

  async carregarDocumentos(codigo) {
    const resp = await fetchComRetry(
      `${BASE_URL}/api/laudos/index.php?codigo=${encodeURIComponent(codigo)}&recurso=documentos`,
      { credentials: 'include' }
    );
    return tratarResposta(resp);
  },

  /* ---------- TAREFAS (novo domínio /api/tarefas/) ---------- */

  async listarTarefas(codigo, filtros = {}) {
    const params = new URLSearchParams({ codigo });
    if (filtros.imovel_id)   params.set('imovel_id',   String(filtros.imovel_id));
    if (filtros.imovel_uuid) params.set('imovel_uuid', String(filtros.imovel_uuid));
    if (filtros.categoria)   params.set('categoria',   String(filtros.categoria));
    if (filtros.status)      params.set('status',      String(filtros.status));
    if (filtros.prioridade)  params.set('prioridade',  String(filtros.prioridade));
    if (filtros.limit)       params.set('limit',       String(filtros.limit));

    const resp = await fetchComRetry(
      `${BASE_URL}/api/tarefas/listarTarefas.php?${params.toString()}`,
      { credentials: 'include' }
    );
    return tratarResposta(resp);
  },

  async concluirTarefa(tarefaId, observacao = '') {
    const resp = await fetchComRetry(`${BASE_URL}/api/tarefas/concluirTarefa.php`, {
      method: 'POST',
      headers: headersBase(),
      credentials: 'include',
      body: JSON.stringify({ tarefa_id: tarefaId, observacao }),
    });
    return tratarResposta(resp);
  },

  async reabrirTarefa(tarefaId, motivo) {
    const resp = await fetchComRetry(`${BASE_URL}/api/tarefas/reabrirTarefa.php`, {
      method: 'POST',
      headers: headersBase(),
      credentials: 'include',
      body: JSON.stringify({ tarefa_id: tarefaId, motivo }),
    });
    return tratarResposta(resp);
  },

  /* ---------- AMOSTRAS ---------- */

  async buscarAmostras(latitude, longitude, raioKm = 1.5) {
    const params = new URLSearchParams({
      lat: String(latitude), lng: String(longitude), radius: String(raioKm),
    });
    const resp = await fetchComRetry(
      `${BASE_URL}/v2/market/samples?${params.toString()}`,
      { credentials: 'include' }
    );
    return tratarResposta(resp);
  },

  /* ---------- PERFIL ---------- */

  async salvarPerfilExpert(payloadExpert, instanciaId) {
    const resp = await fetchComRetry(`${BASE_URL}/api/laudos/salvar.php`, {
      method: 'POST',
      headers: headersBase({}, instanciaId),
      credentials: 'include',
      body: JSON.stringify({
        tipo_modulo: 'PERFIL_EXPERT',
        dados: payloadExpert,
      }),
    });
    return tratarResposta(resp);
  },

  /* ---------- FILA ---------- */

  async sincronizarFila() {
    const fila = lerFila();
    if (fila.length === 0) return { enviados: 0, falhas: 0 };

    const restantes = [];
    let enviados = 0;
    let falhas = 0;

    for (const item of fila) {
      try {
        const { _enfileiradoEm, ...payload } = item;
        await laudoApi.salvarModulo(payload);
        enviados++;
      } catch {
        restantes.push(item);
        falhas++;
      }
    }

    if (restantes.length > 0) {
      localStorage.setItem(FILA_KEY, JSON.stringify(restantes));
    } else {
      localStorage.removeItem(FILA_KEY);
    }

    return { enviados, falhas };
  },
};

export { BASE_URL };
export default laudoApi;